import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { DollarSign, Package, AlertTriangle } from "lucide-react";
import { INVENTORY } from "@/constants/testIds";

const BRAND_COLOR = "#8C7D70";

function ChartCard({ title, data, testId }) {
  return (
    <div className="bg-white border border-border rounded-xl p-5" data-testid={testId}>
      <h4 className="text-sm font-medium text-foreground mb-3">{title}</h4>
      {data.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">Sin consumo registrado todavía.</p>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={data.slice(0, 8)} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E8DFD8" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={50} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip formatter={(value, name) => [name === "cost" ? `$${value.toFixed(2)}` : value, name === "cost" ? "Costo" : "Cantidad"]} />
            <Bar dataKey="cost" fill={BRAND_COLOR} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

/** KPIs de valor de inventario + reportes de consumo por producto, profesional, servicio y mes. */
export default function ReportsTab({ summary, consumption, loading }) {
  if (loading) return <p className="text-center text-muted-foreground py-12">Cargando reportes...</p>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-border rounded-xl p-5 flex items-center gap-3" data-testid={INVENTORY.summaryValueCard}>
          <div className="w-10 h-10 rounded-full bg-brand-light flex items-center justify-center shrink-0">
            <DollarSign className="w-5 h-5 text-brand" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Valor total del inventario</p>
            <p className="font-serif text-2xl text-foreground">${summary.total_inventory_value.toFixed(2)}</p>
          </div>
        </div>
        <div className="bg-white border border-border rounded-xl p-5 flex items-center gap-3" data-testid={INVENTORY.summaryProductsCard}>
          <div className="w-10 h-10 rounded-full bg-brand-light flex items-center justify-center shrink-0">
            <Package className="w-5 h-5 text-brand" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Productos activos</p>
            <p className="font-serif text-2xl text-foreground">{summary.total_products}</p>
          </div>
        </div>
        <div className="bg-white border border-border rounded-xl p-5 flex items-center gap-3" data-testid={INVENTORY.summaryLowStockCard}>
          <div className="w-10 h-10 rounded-full bg-[#F4E1E1] flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-[#A34B49]" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Con stock bajo</p>
            <p className="font-serif text-2xl text-foreground">{summary.low_stock_count}</p>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-xs uppercase tracking-[0.1em] text-muted-foreground font-medium mb-3">Consumo (costo en USD)</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Por producto" data={consumption.by_product} testId={INVENTORY.reportsByProductChart} />
          <ChartCard title="Por profesional" data={consumption.by_professional} testId={INVENTORY.reportsByProfessionalChart} />
          <ChartCard title="Por servicio" data={consumption.by_service} testId={INVENTORY.reportsByServiceChart} />
          <ChartCard title="Por mes" data={consumption.by_month} testId={INVENTORY.reportsByMonthChart} />
        </div>
      </div>
    </div>
  );
}
